import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';

import type { FixedAsset, Apartment } from '@/integrations/supabase/fixedasset.models';

export type { FixedAsset, Apartment };
export type Land = Tables<'lands'>;
export type House = Tables<'houses'> & { linkedLandName?: string };
export type Vehicle = Tables<'vehicles'>;
export type Machinery = Tables<'machinery'>;
export type Equipment = Tables<'equipments'>;
export type CommercialBuilding = Tables<'commercial_buildings'>;

export interface FixedAssetDetail {
  asset: FixedAsset;
  apartment?: Apartment;
  land?: Land;
  house?: House;
  vehicle?: Vehicle;
  machinery?: Machinery;
  equipment?: Equipment;
  commercialBuilding?: CommercialBuilding;
}

export function useFixedAssetDetail(assetId: string | undefined) {
  return useQuery({
    queryKey: ['fixed-asset-detail', assetId],
    queryFn: async (): Promise<FixedAssetDetail> => {
      const { data: asset, error: assetError } = await supabase
        .from('fixed_assets')
        .select('*')
        .eq('id', assetId!)
        .single();

      if (assetError) throw assetError;
      if (!asset) throw new Error('Asset not found');

      const result: FixedAssetDetail = { asset };

      switch (asset.asset_type) {
        case 'apartment': {
          const { data } = await supabase
            .from('apartments')
            .select('*')
            .eq('fixed_asset_id', assetId!)
            .maybeSingle();
          if (data) result.apartment = data;
          break;
        }
        case 'land': {
          const { data } = await supabase
            .from('lands')
            .select('*')
            .eq('fixed_asset_id', assetId!)
            .maybeSingle();
          if (data) result.land = data;
          break;
        }
        case 'house': {
          const { data } = await supabase
            .from('houses')
            .select('*')
            .eq('fixed_asset_id', assetId!)
            .maybeSingle();
          if (data) {
            const house: House = { ...data };
            if (data.land_id) {
              const { data: landData } = await supabase
                .from('lands')
                .select('fixed_asset_id')
                .eq('id', data.land_id)
                .maybeSingle();
              if (landData) {
                const { data: landAsset } = await supabase
                  .from('fixed_assets')
                  .select('name')
                  .eq('id', landData.fixed_asset_id)
                  .maybeSingle();
                house.linkedLandName = landAsset?.name;
              }
            }
            result.house = house;
          }
          break;
        }
        case 'vehicle': {
          const { data } = await supabase
            .from('vehicles')
            .select('*')
            .eq('fixed_asset_id', assetId!)
            .maybeSingle();
          if (data) result.vehicle = data;
          break;
        }
        case 'machinery': {
          const { data } = await supabase
            .from('machinery')
            .select('*')
            .eq('fixed_asset_id', assetId!)
            .maybeSingle();
          if (data) result.machinery = data;
          break;
        }
        case 'equipment': {
          const { data } = await supabase
            .from('equipments')
            .select('*')
            .eq('fixed_asset_id', assetId!)
            .maybeSingle();
          if (data) result.equipment = data;
          break;
        }
        case 'commercial_building': {
          const { data } = await supabase
            .from('commercial_buildings')
            .select('*')
            .eq('fixed_asset_id', assetId!)
            .maybeSingle();
          if (data) result.commercialBuilding = data;
          break;
        }
      }

      return result;
    },
    enabled: !!assetId,
  });
}
